import { useEffect, useState } from "react";

import { fetchOrgPublisherMap } from "@/lib/publisher-map-api";

/** The two places a seller can land. */
export const TRACKS_LANDING = "/tracks";
export const COMPANY_HUB_LANDING = "/corporate-hierarchy";

/** How long the root redirect waits for the answer before sending the seller to Tracks as before. */
export const LANDING_WAIT_MS = 2500;

/**
 * Where a signed-in seller lands: a group set up on the publisher map (Believe first) opens on Your
 * Companies, its company hub; every other seller opens on Tracks as before.
 *
 * The question is one cheap read (`GET /api/org/publisher-map`, a file on the server), asked only when
 * `enabled`. Anything that goes wrong, or takes longer than `LANDING_WAIT_MS`, answers Tracks: the hub is
 * a better first page for Believe, but a slow or failed check must never hold a sign-in.
 */
export function useSellerLanding(enabled: boolean): { landing: string | null } {
  const [landing, setLanding] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const fallback = window.setTimeout(() => {
      controller.abort();
      setLanding((cur) => cur ?? TRACKS_LANDING);
    }, LANDING_WAIT_MS);
    (async () => {
      let next = TRACKS_LANDING;
      try {
        const res = await fetchOrgPublisherMap(controller.signal);
        next = res.linked ? COMPANY_HUB_LANDING : TRACKS_LANDING;
      } catch {
        // A failed check lands on Tracks, as before this hook existed.
      }
      window.clearTimeout(fallback);
      if (!controller.signal.aborted) setLanding(next);
    })();
    return () => {
      controller.abort();
      window.clearTimeout(fallback);
    };
  }, [enabled]);

  return { landing: enabled ? landing : TRACKS_LANDING };
}
